import { useEffect, useState } from "react";
import { Flame, Scale, Zap } from "lucide-react";
import { getAnalisiPratica } from "@/api/analisiBolletta";
import type { BollettaPratica, FornituraAnalisi, OffertaAnalisi } from "@/api/analisiBolletta";
import {
  consumoAnnuo, differenza, euro, meseAnno, motivoBolletta, motivoOfferta,
  prezzoOfferta, quotaMese,
} from "@/lib/analisiScritte";

const COLONNE = "sm:grid sm:grid-cols-[minmax(0,1fr)_9rem_11rem] sm:items-baseline sm:gap-x-4";
const VERSO = {
  meno: "text-brand-dark font-medium",
  piu: "text-amber-800 font-medium",
  pari: "text-muted-foreground",
};

function meseDellaBolletta(f: FornituraAnalisi | undefined): string | null {
  const data = f?.periodo_a || f?.periodo_da;
  return data ? meseAnno(data.slice(0, 7)) : null;
}

function Offerta({ o, f }: { o: OffertaAnalisi; f: FornituraAnalisi }) {
  const dettaglio = o.prezzo && o.quota_fissa_mese
    ? `${prezzoOfferta(o.prezzo, o.tipo_prezzo, f.commodity)} · ${quotaMese(o.quota_fissa_mese)}`
    : null;
  const confronto = o.spesa_annua !== null && o.differenza !== null;
  const d = confronto ? differenza(o.differenza as string) : null;
  return (
    <div role="row" className={`${COLONNE} py-2.5 space-y-1 sm:space-y-0`}>
      <div role="cell">
        <p className="font-medium text-text">{o.nome}</p>
        {dettaglio && <p className="text-xs text-muted-foreground">{dettaglio}</p>}
      </div>
      {d ? (
        <>
          <div role="cell" className="flex justify-between sm:block sm:text-right">
            <span className="text-xs text-muted-foreground sm:hidden">Spesa all'anno</span>
            <span>{euro(o.spesa_annua as string)}</span>
          </div>
          <div role="cell" className="flex justify-between sm:block sm:text-right">
            <span className="text-xs text-muted-foreground sm:hidden">Differenza</span>
            <span className={VERSO[d.verso]}>{d.testo}</span>
          </div>
        </>
      ) : (
        <p role="cell" className="text-xs text-muted-foreground sm:col-span-2">
          {motivoOfferta(o.motivo, {
            commodity: f.commodity,
            tipoBolletta: f.tipo_prezzo,
            tipoOfferta: o.tipo_prezzo,
            riferimentoMercato: f.riferimento_mercato,
          })}
        </p>
      )}
    </div>
  );
}

function Fornitura({ f, intestazione }: { f: FornituraAnalisi; intestazione: boolean }) {
  const Icona = f.commodity === "gas" ? Flame : Zap;
  const unita = f.commodity === "gas" ? "Smc" : "kWh";
  return (
    <div className="space-y-3">
      {intestazione && (
        <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Icona className="h-3.5 w-3.5" aria-hidden="true" />
          {f.commodity === "gas" ? "Gas" : "Luce"}
        </p>
      )}
      {f.motivo || f.oggi_paghi === null ? (
        <p className="text-sm text-muted-foreground">{motivoBolletta(f.motivo)}</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-[220px_1fr]">
          <div className="rounded-lg bg-surface p-4">
            <p className="text-sm text-muted-foreground">Spesa attuale</p>
            <p className="text-3xl font-semibold text-text tabular-nums">{euro(f.oggi_paghi)}</p>
            <p className="text-sm text-muted-foreground">all'anno</p>
            {f.consumo_annuo && f.consumo_annuo_fonte && (
              <p className="mt-2 text-xs text-muted-foreground">
                {consumoAnnuo(f.consumo_annuo, f.consumo_annuo_fonte, unita)}
              </p>
            )}
          </div>
          {f.offerte.length === 0 ? (
            <p className="text-sm text-muted-foreground self-center">{motivoBolletta(null)}</p>
          ) : (
          <div role="table" className="text-sm tabular-nums">
            <div
              role="row"
              className={`hidden ${COLONNE} border-b border-border pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground`}
            >
              <span role="columnheader">Offerta</span>
              <span role="columnheader" className="text-right">Spesa all'anno</span>
              <span role="columnheader" className="text-right">Differenza</span>
            </div>
            <div role="rowgroup" className="divide-y divide-border">
              {f.offerte.map((o) => <Offerta key={o.id} o={o} f={f} />)}
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SchedaSpesaVenditaPratica({ oppId }: { oppId: string }) {
  const [pronte, setPronte] = useState<BollettaPratica[]>([]);

  useEffect(() => {
    let annullato = false;
    getAnalisiPratica(oppId)
      .then((r) => {
        if (annullato || !r.attiva) return;
        setPronte(r.bollette.filter((b) => b.stato === "pronta" && b.risultato));
      })
      .catch(() => { /* riquadro opzionale: in errore la pagina resta com'è */ });
    return () => { annullato = true; };
  }, [oppId]);

  if (pronte.length === 0) return null;
  const conCifre = pronte.some((b) => b.risultato?.forniture.some((f) => !f.motivo && f.oggi_paghi !== null));

  return (
    <div className="bg-card p-4 rounded-lg border border-border space-y-4">
      <h3 className="flex items-center gap-2 font-semibold text-text">
        <Scale className="h-5 w-5 text-brand" aria-hidden="true" />
        Spesa per la vendita
      </h3>
      {pronte.map((b) => {
        const forniture = b.risultato?.forniture ?? [];
        const mese = meseDellaBolletta(forniture.find((f) => f.periodo_a || f.periodo_da));
        return (
          <div key={b.cbolletta_id} className="space-y-4">
            {mese && <p className="text-xs text-muted-foreground">dalla bolletta di {mese}</p>}
            {forniture.length === 0 && (
              <p className="text-sm text-muted-foreground">{motivoBolletta(b.risultato?.motivo ?? null)}</p>
            )}
            {forniture.map((f, i) => (
              <Fornitura key={`${f.commodity}-${i}`} f={f} intestazione={forniture.length > 1} />
            ))}
          </div>
        );
      })}
      {conCifre && (
        <p className="text-xs text-muted-foreground">
          Solo spesa per l'energia, IVA esclusa, a parità di consumi annui.
        </p>
      )}
    </div>
  );
}
